import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
    Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, IconButton, Typography, Alert, AlertTitle, MenuItem, Select, 
    TextField, Autocomplete, Popper
} from '@mui/material';
import {
    DataGridPro,
    GridToolbar,
    GridActionsCellItem,
    useGridApiContext
} from '@mui/x-data-grid-pro';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import { toast } from 'react-toastify';
import axios from 'axios';
import Papa from 'papaparse';
import dayjs from 'dayjs';

const api = axios.create({ withCredentials: true });

// Dropdown options for lookup fields (text label -> numeric ID)
// Source: TN POS API v13 Appendix I - Lookup Tables
const LOOKUP_OPTIONS = {
    seating_type: [
        { id: 1, label: 'Odd/Even' },
        { id: 2, label: 'Consecutive' },
        { id: 3, label: 'General Admission' }
    ],
    stock_type: [
        { id: 1, label: 'Standard' },
        { id: 2, label: 'e-Ticket' },
        { id: 3, label: 'Commemorative' },
        { id: 4, label: 'Paperless' },
        { id: 5, label: 'AXS Mobile ID' },
        { id: 7, label: 'Mobile' }
    ],
    ticket_group_type: [
        { id: 1, label: 'Event Ticket' },
        { id: 2, label: 'Suite' },
        { id: 3, label: 'Parking' },
        { id: 4, label: 'Hotel' },
        { id: 5, label: 'Package' },
        { id: 6, label: 'Backstage Pass' },
        { id: 7, label: 'Pit Pass' },
        { id: 8, label: 'VIP Pass' },
        { id: 9, label: 'Meet/Greet Pass' },
        { id: 10, label: 'Pass' }
    ],
    shipping_method: [
        { id: 1, label: 'FedEx Intl Priority' },
        { id: 2, label: 'FedEx Economy' },
        { id: 3, label: 'FedEx Intl First' },
        { id: 4, label: 'FedEx Intl Priority Freight' },
        { id: 5, label: 'FedEx Intl Economy Freight' },
        { id: 6, label: 'FedEx Priority Overnight' },
        { id: 7, label: 'FedEx 2nd Day' },
        { id: 8, label: 'FedEx Standard Overnight' },
        { id: 9, label: 'FedEx First Overnight' },
        { id: 10, label: 'FedEx Express Saver' },
        { id: 11, label: 'FedEx Saturday Delivery' },
        { id: 12, label: 'FedEx 2day Freight' },
        { id: 13, label: 'FedEx Express Saver Freight' },
        { id: 14, label: 'Local Pickup Near Venue' },
        { id: 15, label: 'FedEx Home/Ground Delivery' },
        { id: 16, label: 'Courier' },
        { id: 17, label: 'Retail' },
        { id: 18, label: 'Will Call' },
        { id: 19, label: 'Regular Mail' },
        { id: 20, label: 'Certified Mail' },
        { id: 21, label: 'Express Delivery' },
        { id: 22, label: 'E-Ticket' },
        { id: 23, label: 'Paperless' },
        { id: 24, label: 'Electronic Transfer' },
        { id: 25, label: 'Last Minute Pickup' },
        { id: 26, label: '3rd Party Label' },
        { id: 27, label: 'UPS' },
        { id: 28, label: 'TN Digital Conversion' },
        { id: 29, label: 'Paperless - Meet Customer' },
        { id: 30, label: 'Paperless - Ship Gift Card' },
        { id: 31, label: 'Direct Transfer' },
        { id: 32, label: 'Upload QR Screenshot' },
        { id: 33, label: 'AXS Mobile ID' }
    ],
    payment_type: [
        { id: 1, label: 'Credit Card' },
        { id: 2, label: 'On Account' },
        { id: 5, label: 'Money Order' },
        { id: 7, label: 'COD' },
        { id: 8, label: 'Refund' },
        { id: 9, label: 'Cash' },
        { id: 10, label: 'ACH' },
        { id: 11, label: 'Trade (Non-Ticket Swap)' },
        { id: 12, label: 'Swap (ticket-for-ticket)' },
        { id: 13, label: 'Check (unsecured)' },
        { id: 14, label: 'BillPoint' },
        { id: 15, label: 'PayPal' },
        { id: 16, label: 'Check (secured)' },
        { id: 17, label: 'Credit/Gift Certificate' },
        { id: 18, label: 'Comp' },
        { id: 19, label: 'Write off/Bad debt' },
        { id: 20, label: 'Credit Card - Manual' },
        { id: 21, label: 'ITEX' },
        { id: 22, label: 'TN Payments' }
    ],
    shipping_method_special: [
        { id: 0, label: 'Near Term Special Delivery' },
        { id: 1, label: 'E-Ticket' },
        { id: 2, label: 'Will Call' },
        { id: 3, label: 'Local Pickup Near Venue' },
        { id: 10, label: 'TN Digital Conversion' },
        { id: 11, label: 'Paperless - Meet Customer' },
        { id: 12, label: 'Paperless - Ship Gift Card' },
        { id: 13, label: 'Direct Transfer' },
        { id: 14, label: 'Upload QR Screenshot' },
        { id: 15, label: 'AXS Mobile ID' }
    ],
    exposure_type: [
        { id: 1, label: 'Show All Details' },
        { id: 2, label: 'Hide Seats' },
        { id: 3, label: 'Hide Rows' },
        { id: 4, label: 'Hide Rows and Seats' }
    ],
    split_rule: [
        { id: 1, label: 'All Splits' },
        { id: 2, label: 'No Splits' }
    ],
    near_term_display_option: [
        { id: 0, label: 'Default near-term display options' },
        { id: 2, label: 'Only show near-term shipping' }
    ]
};

// Custom edit component for Select dropdowns
function SelectEditCell(props) {
    const { id, value, field, options } = props;
    const apiRef = useGridApiContext();

    const handleChange = (event) => {
        apiRef.current.setEditCellValue({ id, field, value: event.target.value });
    };

    return (
        <Select
            value={value || ''}
            onChange={handleChange}
            size="small"
            fullWidth
            autoFocus
        >
            <MenuItem value="">None</MenuItem>
            {options.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                    {opt.label}
                </MenuItem>
            ))}
        </Select>
    );
}

// Custom edit component for Event autocomplete
function EventEditCell(props) {
    const { id, value, field, api, onRowUpdate } = props;
    const apiRef = useGridApiContext();
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [inputValue, setInputValue] = useState(value || '');
    const [selectedValue, setSelectedValue] = useState(value ? { label: value, id: null } : null);
    const [open, setOpen] = useState(false);

    const handleSearch = async (searchValue) => {
        if (!searchValue || searchValue.length < 3) {
            setOptions([]);
            return;
        }

        setLoading(true);
        const results = await api.searchEvents(searchValue);
        setOptions(results);
        setLoading(false);
    };

    const handleChange = (event, newValue) => {
        if (newValue) {
            // Store the selected value and input value locally first
            setSelectedValue(newValue);
            setInputValue(newValue.label);
            
            // Update the DataGrid cell value for event_name
            apiRef.current.setEditCellValue({ id, field: 'event_name', value: newValue.label });
            
            // Update the row directly via parent callback for event_id
            if (onRowUpdate) {
                onRowUpdate(id, {
                    event_name: newValue.label,
                    event_id: newValue.id
                });
            }
        }
    };

    return (
        <Autocomplete
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            value={selectedValue}
            inputValue={inputValue}
            onInputChange={(e, newValue) => {
                setInputValue(newValue);
                handleSearch(newValue);
            }}
            onChange={handleChange}
            options={options}
            loading={loading}
            getOptionLabel={(option) => option.label || ''}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder="Type event name..."
                    size="small"
                    autoFocus
                    fullWidth
                />
            )}
            noOptionsText={inputValue.length < 3 ? "Type 3+ characters to search" : "No events found"}
            PopperComponent={(popperProps) => (
                <Popper {...popperProps} style={{ width: 500, zIndex: 9999 }} placement="bottom-start" />
            )}
            componentsProps={{
                paper: {
                    sx: {
                        width: 500
                    }
                }
            }}
            fullWidth
            openOnFocus={false}
            sx={{
                '& .MuiAutocomplete-input': {
                    fontSize: '0.875rem'
                }
            }}
        />
    );
}

// Main Component
function POCSVUpload({ open, onClose, current_broker, current_user }) {
    const { getAccessTokenSilently } = useAuth0();
    
    // State management
    const [rows, setRows] = useState([]);
    const [rowIdCounter, setRowIdCounter] = useState(1);
    const [submitting, setSubmitting] = useState(false);
    const [matchingDialogOpen, setMatchingDialogOpen] = useState(false);
    const [pendingImport, setPendingImport] = useState([]);
    const [resultsDialogOpen, setResultsDialogOpen] = useState(false);
    const [submissionResults, setSubmissionResults] = useState([]);

    // Initialize with empty rows on open
    useEffect(() => {
        if (open && rows.length === 0) {
            initializeEmptyRows();
        }
    }, [open]);

    const initializeEmptyRows = () => {
        const emptyRows = Array.from({ length: 10 }, (_, i) => createEmptyRow(i + 1));
        setRows(emptyRows);
        setRowIdCounter(11);
    };

    const createEmptyRow = (id) => ({
        id,
        // Hidden PO-level fields (required by API)
        office_id: 1, // REQUIRED - System office ID (1 if no multiple offices)
        csr_id: 1, // Optional - CSR ID (1 = System Account)
        currency_id: 1, // Optional - Currency (1 = USD)
        
        // Event identification
        event_id: null, // REQUIRED - TN Event ID
        event_name: '',
        
        // Vendor
        vendor_id: current_broker !== 'all' ? current_broker : null, // REQUIRED - Auto-populate with current broker
        
        // Seat location (REQUIRED)
        section: '', // REQUIRED
        row: '', // REQUIRED
        low_seat: null, // REQUIRED - Should be number
        high_seat: null, // Optional - Should be number, auto-calculates for consecutive
        quantity: 1, // REQUIRED - Must be >= 1
        
        // Pricing (at least one should be > 0)
        face_price: 0, // Optional - Face value per ticket
        retail_price: 0, // Optional - Retail price per ticket
        wholesale_price: 0, // Optional - Wholesale price per ticket
        ticket_group_cost: 0, // Optional - Total cost for entire ticket group
        taxed_cost: 0, // Optional - Tax already paid
        msrp: 0, // Optional - All-In Venue Price
        
        // Ticket type lookups (REQUIRED)
        seating_type: 2, // REQUIRED - 2=Consecutive (default)
        stock_type: 1, // Optional - 1=Standard (API default), 2=E-Ticket
        ticket_group_type: 1, // REQUIRED - 1=Event Ticket (default)
        exposure_type: 1, // REQUIRED - 1=Show All (default)
        split_rule: 1, // Optional - 1=All Splits (default), auto-set to 2 for parking
        
        // Shipping & Payment
        shipping_method: 22, // REQUIRED at PO level - 22=E-Ticket (default)
        payment_type: 2, // REQUIRED at PO level - 2=ACH (default)
        shipping_method_special: null, // Optional - Special shipping specification
        near_term_display_option: null, // Optional - Near term display setting
        
        // Inventory status
        on_hand: true, // Optional - Default true (tickets physically on hand)
        on_hand_date: null, // REQUIRED if on_hand=false, cannot be future if on_hand=true
        
        // Additional fields
        ticket_group_code_id: null, // Optional - Ticket group code ID
        paid_in_full: false, // Optional - PO payment status
        external_po_number: '', // Optional - External PO reference number
        
        // Notes fields
        internal_po_notes: '', // Optional - Internal PO notes
        external_po_notes: '', // Optional - External PO notes
        internal_tg_notes: '', // Optional - Internal ticket group notes
        external_tg_notes: '', // Optional - External ticket group notes
        pricing_notes: '', // Optional - Pricing notes
        
        // Broadcast
        broadcast_channel_ids: '1,2,3,4,6' // Optional - Comma-separated broadcast channel IDs (default POS behavior)
    });

    const searchEvents = async (query) => {
        if (!query || query.length < 3) return [];

        try {
            const token = await getAccessTokenSilently({
                audience: process.env.REACT_APP_AUTH0AUDIENCE,
                scope: process.env.REACT_APP_AUTH0SCOPE
            });

            const response = await api.get(
                `${process.env.REACT_APP_BASEURL}/api/tn/events/search`,
                {
                    params: { eventName: `*${query}*` }, // Add wildcards for partial matching
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.data && response.data.Events) {
                return response.data.Events.map(event => ({
                    id: event.EventID,
                    label: `${event.EventName} - ${dayjs(event.EventDateAndTime).format('MM/DD/YYYY')} @ ${event.VenueName}`,
                    name: event.EventName,
                    date: event.EventDateAndTime,
                    venue: event.VenueName
                }));
            }
            return [];
        } catch (error) {
            console.error('Error searching events:', error);
            return [];
        }
    };

    const handleAddRow = () => {
        const newRow = createEmptyRow(rowIdCounter);
        setRows([...rows, newRow]);
        setRowIdCounter(rowIdCounter + 1);
    };

    const handleDeleteRow = (id) => {
        setRows(rows.filter(row => row.id !== id));
    };

    const handleProcessRowUpdate = (newRow) => {
        const updatedRows = rows.map(row => row.id === newRow.id ? newRow : row);
        setRows(updatedRows);
        return newRow;
    };

    const handleRowUpdate = (rowId, updatedFields) => {
        console.log('handleRowUpdate called:', rowId, updatedFields);
        setRows(prevRows => 
            prevRows.map(row => 
                row.id === rowId ? { ...row, ...updatedFields } : row
            )
        );
    };

    const handleCSVUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: async (results) => {
                if (results.data && results.data.length > 0) {
                    toast.info('Searching for events... This may take a moment.');
                    
                    const token = await getAccessTokenSilently({
                        audience: process.env.REACT_APP_AUTH0AUDIENCE,
                        scope: process.env.REACT_APP_AUTH0SCOPE
                    });
                    
                    // Helper function to normalize venue names for matching
                    const normalizeVenue = (venue) => {
                        if (!venue) return '';
                        return venue.toLowerCase()
                            .replace(/\s+/g, ' ') // normalize whitespace
                            .replace(/[^a-z0-9\s]/g, '') // remove special chars
                            .trim();
                    };
                    
                    // Helper function to parse various date formats
                    const parseFlexibleDate = (dateStr) => {
                        if (!dateStr) return null;
                        
                        // Try various common formats
                        const formats = [
                            'YYYY-MM-DD',
                            'MM/DD/YYYY',
                            'MM-DD-YYYY',
                            'M/D/YYYY',
                            'M-D-YYYY',
                            'YYYY/MM/DD',
                            'DD/MM/YYYY',
                            'DD-MM-YYYY',
                            'MMM DD YYYY',
                            'MMM DD, YYYY',
                            'MMMM DD YYYY',
                            'MMMM DD, YYYY'
                        ];
                        
                        for (const format of formats) {
                            const parsed = dayjs(dateStr, format, true);
                            if (parsed.isValid()) {
                                return parsed.format('YYYY-MM-DD');
                            }
                        }
                        
                        // Last resort - let dayjs try auto-parse
                        const autoParsed = dayjs(dateStr);
                        if (autoParsed.isValid()) {
                            return autoParsed.format('YYYY-MM-DD');
                        }
                        
                        return null;
                    };
                    
                    // Process each row and search for matching events
                    const processedRows = await Promise.all(
                        results.data.map(async (csvRow, index) => {
                            let matchedEvents = [];
                            let selectedEvent = null;
                            
                            // Try to find event by name/venue/date if provided (no event_id)
                            if (!csvRow.event_id && csvRow.event_name) {
                                try {
                                    // Build search query - start with event name only for broader results
                                    let searchQuery = csvRow.event_name.trim();
                                    
                                    // Search for events
                                    const searchResponse = await api.get(
                                        `${process.env.REACT_APP_BASEURL}/api/tn/events/search`,
                                        {
                                            params: { eventName: `*${searchQuery}*` },
                                            headers: { Authorization: `Bearer ${token}` }
                                        }
                                    );
                                    
                                    if (searchResponse.data && searchResponse.data.Events) {
                                        matchedEvents = searchResponse.data.Events.map(event => ({
                                            id: event.EventID,
                                            label: `${event.EventName} - ${dayjs(event.EventDateAndTime).format('MM/DD/YYYY')} @ ${event.VenueName}`,
                                            name: event.EventName,
                                            date: event.EventDateAndTime,
                                            venue: event.VenueName
                                        }));
                                        
                                        // Filter by venue and/or date if provided
                                        let filteredEvents = [...matchedEvents];
                                        
                                        // Venue filtering with flexible matching
                                        if (csvRow.event_venue) {
                                            const venueInput = normalizeVenue(csvRow.event_venue);
                                            filteredEvents = filteredEvents.filter(event => {
                                                const eventVenue = normalizeVenue(event.venue);
                                                // Check if either contains the other (bidirectional partial match)
                                                return eventVenue.includes(venueInput) || venueInput.includes(eventVenue);
                                            });
                                        }
                                        
                                        // Date filtering with flexible parsing
                                        if (csvRow.event_date) {
                                            const parsedDate = parseFlexibleDate(csvRow.event_date);
                                            if (parsedDate) {
                                                filteredEvents = filteredEvents.filter(event => {
                                                    const eventDate = dayjs(event.date).format('YYYY-MM-DD');
                                                    return eventDate === parsedDate;
                                                });
                                            }
                                        }
                                        
                                        // Use filtered results if we got any, otherwise keep all matches
                                        if (filteredEvents.length > 0) {
                                            matchedEvents = filteredEvents;
                                        }
                                        
                                        // Auto-select if only one match
                                        if (matchedEvents.length === 1) {
                                            selectedEvent = matchedEvents[0];
                                        }
                                    }
                                } catch (error) {
                                    console.error('Error searching for event:', error);
                                }
                            }
                            
                            return {
                                rowIndex: index,
                                csvRow,
                                matchedEvents,
                                selectedEvent,
                                confirmed: !!selectedEvent || !!csvRow.event_id
                            };
                        })
                    );
                    
                    // Check if we need user confirmation
                    const needsConfirmation = processedRows.some(row => 
                        !row.confirmed || row.matchedEvents.length > 1
                    );
                    
                    if (needsConfirmation) {
                        setPendingImport(processedRows);
                        setMatchingDialogOpen(true);
                    } else {
                        // All matched automatically, import directly
                        importConfirmedRows(processedRows);
                    }
                }
            },
            error: (error) => {
                console.error('CSV Parse Error:', error);
                toast.error('Failed to parse CSV file');
            }
        });
        
        // Reset file input
        event.target.value = '';
    };
    
    const importConfirmedRows = (processedRows) => {
        const importedRows = processedRows.map((processed, index) => {
            const csvRow = processed.csvRow;
            const event = processed.selectedEvent;
            
            return {
                id: rowIdCounter + index,
                
                // Hidden PO-level fields
                office_id: parseInt(csvRow.office_id) || 1,
                csr_id: parseInt(csvRow.csr_id) || 1,
                currency_id: parseInt(csvRow.currency_id) || 1,
                
                // Event identification
                event_id: event ? event.id : (parseInt(csvRow.event_id) || null),
                event_name: event ? event.label : (csvRow.event_name || ''),
                vendor_id: current_broker !== 'all' ? current_broker : null,
                
                // Seat location
                section: csvRow.section || '',
                row: csvRow.row || '',
                low_seat: parseInt(csvRow.low_seat) || null,
                high_seat: parseInt(csvRow.high_seat) || null,
                quantity: parseInt(csvRow.quantity) || 1,
                
                // Pricing
                face_price: parseFloat(csvRow.face_price) || 0,
                retail_price: parseFloat(csvRow.retail_price) || 0,
                wholesale_price: parseFloat(csvRow.wholesale_price) || 0,
                ticket_group_cost: parseFloat(csvRow.ticket_group_cost) || 0,
                taxed_cost: parseFloat(csvRow.taxed_cost) || 0,
                msrp: parseFloat(csvRow.msrp) || 0,
                
                // Ticket type lookups
                seating_type: parseInt(csvRow.seating_type) || 2,
                stock_type: parseInt(csvRow.stock_type) || 1,
                ticket_group_type: parseInt(csvRow.ticket_group_type) || 1,
                exposure_type: parseInt(csvRow.exposure_type) || 1,
                split_rule: parseInt(csvRow.split_rule) || 1,
                
                // Shipping & Payment
                shipping_method: parseInt(csvRow.shipping_method) || 22,
                payment_type: parseInt(csvRow.payment_type) || 2,
                shipping_method_special: parseInt(csvRow.shipping_method_special) || null,
                near_term_display_option: parseInt(csvRow.near_term_display_option) || null,
                
                // Inventory status
                on_hand: csvRow.on_hand !== 'no' && csvRow.on_hand !== 'false' && csvRow.on_hand !== '0',
                on_hand_date: csvRow.on_hand_date || null,
                
                // Additional fields
                ticket_group_code_id: parseInt(csvRow.ticket_group_code_id) || null,
                paid_in_full: csvRow.paid_in_full === 'yes' || csvRow.paid_in_full === 'true' || csvRow.paid_in_full === '1',
                external_po_number: csvRow.external_po_number || '',
                
                // Notes
                internal_po_notes: csvRow.internal_po_notes || '',
                external_po_notes: csvRow.external_po_notes || '',
                internal_tg_notes: csvRow.internal_tg_notes || '',
                external_tg_notes: csvRow.external_tg_notes || '',
                pricing_notes: csvRow.pricing_notes || '',
                
                // Broadcast
                broadcast_channel_ids: csvRow.broadcast_channel_ids || '1,2,3,4,6'
            };
        });
        
        setRows(importedRows);
        setRowIdCounter(rowIdCounter + importedRows.length);
        toast.success(`Imported ${importedRows.length} rows from CSV`);
        setMatchingDialogOpen(false);
        setPendingImport([]);
    };

    const handleDownloadTemplate = () => {
        const headers = [
            // Event (name/date/venue for CSV matching, or event_id if known)
            'event_name', 'event_date', 'event_venue',
            // Seat Location (REQUIRED)
            'section', 'row', 'low_seat', 'high_seat', 'quantity',
            // Pricing
            'retail_price', 'wholesale_price', 'face_price', 'ticket_group_cost', 'taxed_cost', 'msrp',
            // Ticket Type Lookups (REQUIRED)
            'seating_type', 'stock_type', 'ticket_group_type', 'exposure_type', 'split_rule',
            // Shipping & Payment
            'shipping_method', 'payment_type', 'shipping_method_special', 'near_term_display_option',
            // Inventory Status
            'on_hand', 'on_hand_date',
            // Additional Fields
            'ticket_group_code_id', 'paid_in_full', 'external_po_number',
            // Notes
            'internal_po_notes', 'external_po_notes', 'internal_tg_notes', 'external_tg_notes', 'pricing_notes',
            // Broadcast
            'broadcast_channel_ids',
            // Hidden PO Fields (auto-populated, usually don't need to set)
            'office_id', 'csr_id', 'currency_id'
        ];
        
        const csv = Papa.unparse({ fields: headers, data: [] });
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'po_bulk_template.csv';
        link.click();
    };

    const validateAndSubmit = async () => {
        // Filter out completely empty rows
        const filledRows = rows.filter(row => 
            row.event_id || row.section || row.quantity > 1
        );

        if (filledRows.length === 0) {
            toast.error('Please fill in at least one row');
            return;
        }

        // Validate required fields
        const errors = [];
        filledRows.forEach((row, index) => {
            const rowNum = index + 1;
            
            // Required core fields
            if (!row.event_id) errors.push(`Row ${rowNum}: Event is required`);
            if (!row.section || !row.section.trim()) errors.push(`Row ${rowNum}: Section is required`);
            if (!row.row || !row.row.trim()) errors.push(`Row ${rowNum}: Row is required`);
            if (!row.low_seat) errors.push(`Row ${rowNum}: Low Seat is required`);
            if (!row.quantity || row.quantity < 1) errors.push(`Row ${rowNum}: Quantity must be at least 1`);
            
            // Required lookup fields
            if (!row.seating_type) errors.push(`Row ${rowNum}: Seating Type is required`);
            if (!row.ticket_group_type) errors.push(`Row ${rowNum}: Ticket Type is required`);
            if (!row.exposure_type) errors.push(`Row ${rowNum}: Exposure is required`);
            if (!row.shipping_method) errors.push(`Row ${rowNum}: Shipping Method is required`);
            if (!row.payment_type) errors.push(`Row ${rowNum}: Payment Type is required`);
            
            // At least one price field must be > 0
            const hasPricing = (row.retail_price > 0) || (row.wholesale_price > 0) || (row.face_price > 0);
            if (!hasPricing) errors.push(`Row ${rowNum}: At least one price (Retail, Wholesale, or Face) must be greater than 0`);
            
            // OnHandDate validation
            if (!row.on_hand && !row.on_hand_date) {
                errors.push(`Row ${rowNum}: On Hand Date is required when tickets are not on hand`);
            }
            if (row.on_hand && row.on_hand_date) {
                const onHandDate = new Date(row.on_hand_date);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                if (onHandDate > today) {
                    errors.push(`Row ${rowNum}: On Hand Date cannot be in the future when tickets are marked as on hand`);
                }
            }
            
            // Auto-set split rule to "No Splits" for parking
            if (row.ticket_group_type === 3 && row.split_rule !== 2) {
                row.split_rule = 2; // Parking always uses No Splits
            }
        });

        if (errors.length > 0) {
            toast.error(`Validation errors:\n${errors.slice(0, 5).join('\n')}`);
            return;
        }

        setSubmitting(true);
        const results = [];

        try {
            const token = await getAccessTokenSilently({
                audience: process.env.REACT_APP_AUTH0AUDIENCE,
                scope: process.env.REACT_APP_AUTH0SCOPE
            });

            // Submit each row individually and collect results
            for (let i = 0; i < filledRows.length; i++) {
                const row = filledRows[i];
                const rowNum = i + 1;
                
                try {
                    console.log('Submitting row:', { event_id: row.event_id, event_name: row.event_name, section: row.section });
                    
                    // Build request matching the TN API format
                    const ticketGroup = {
                        EventID: row.event_id,
                        PosEventId: null,
                        Section: row.section,
                        Row: row.row,
                        LowSeat: parseInt(row.low_seat),
                        Quantity: parseInt(row.quantity),
                        FacePrice: parseFloat(row.face_price) || 0,
                        WholesalePrice: parseFloat(row.wholesale_price) || 0,
                        RetailPrice: parseFloat(row.retail_price) || 0,
                        TicketGroupCost: parseFloat(row.ticket_group_cost) || 0,
                        SeatingTypeID: parseInt(row.seating_type),
                        StockTypeID: parseInt(row.stock_type),
                        OnHand: row.on_hand,
                        OnHandDate: row.on_hand_date || dayjs().format(),
                        TicketGroupTypeID: parseInt(row.ticket_group_type),
                        InternalTGNotes: row.internal_tg_notes || '',
                        ExternalTGNotes: row.external_tg_notes || '',
                        PricingNotes: row.pricing_notes || '',
                        ExposureTypeID: parseInt(row.exposure_type),
                        BroadcastChannelIDs: row.broadcast_channel_ids || '1,2,3,4,6',
                        SplitRuleID: parseInt(row.split_rule)
                    };
                    
                    // Only add optional fields if they have values
                    if (row.shipping_method_special) {
                        ticketGroup.ShippingMethodSpecialID = parseInt(row.shipping_method_special);
                    }
                    if (row.near_term_display_option) {
                        ticketGroup.NearTermDisplayOptionID = parseInt(row.near_term_display_option);
                    }
                    
                    const purchaseOrderData = {
                        customer_id: row.vendor_id,
                        currency_id: row.currency_id || 1,
                        internal_po_notes: row.internal_po_notes || '',
                        external_po_notes: row.external_po_notes || '',
                        shipping_method_id: row.shipping_method,
                        csr_id: row.csr_id || 1,
                        office_id: row.office_id || 1,
                        tm_order_id: '',
                        vendor_id: row.vendor_id,
                        paid_in_full: row.paid_in_full || false,
                        payment_type_id: row.payment_type,
                        external_po_number: row.external_po_number || '',
                        ticket_groups: [ticketGroup],
                        user: current_user
                    };

                    const response = await api.post(
                        `${process.env.REACT_APP_BASEURL}/api/tn/purchase-order`,
                        purchaseOrderData,
                        { headers: { Authorization: `Bearer ${token}` } }
                    );

                    if (response.data && response.data.Success) {
                        results.push({
                            rowNumber: rowNum,
                            status: 'success',
                            eventName: row.event_name,
                            section: row.section,
                            row: row.row,
                            quantity: row.quantity,
                            poId: response.data.PurchaseOrder?.id || 'N/A',
                            message: 'Successfully created'
                        });
                    } else {
                        results.push({
                            rowNumber: rowNum,
                            status: 'error',
                            eventName: row.event_name,
                            section: row.section,
                            row: row.row,
                            quantity: row.quantity,
                            error: response.data.Message || 'Unknown error',
                            details: response.data
                        });
                    }
                } catch (error) {
                    console.error(`Error creating PO for row ${rowNum}:`, error);
                    
                    // Extract detailed error message from API response
                    let errorMessage = 'Failed to create purchase order';
                    let errorDetails = null;
                    
                    if (error.response?.data) {
                        errorMessage = error.response.data.Message || errorMessage;
                        errorDetails = error.response.data;
                    } else if (error.message) {
                        errorMessage = error.message;
                    }
                    
                    results.push({
                        rowNumber: rowNum,
                        status: 'error',
                        eventName: row.event_name,
                        section: row.section,
                        row: row.row,
                        quantity: row.quantity,
                        error: errorMessage,
                        details: errorDetails
                    });
                }
            }

            // Store results and show dialog
            setSubmissionResults(results);
            setResultsDialogOpen(true);
            
            const successCount = results.filter(r => r.status === 'success').length;
            const errorCount = results.filter(r => r.status === 'error').length;
            
            // If all successful, can close main dialog after viewing results
            if (errorCount === 0) {
                toast.success(`Successfully created ${successCount} purchase order(s)`);
            } else if (successCount > 0) {
                toast.warning(`Created ${successCount} purchase order(s), ${errorCount} failed`);
            } else {
                toast.error('Failed to create any purchase orders');
            }
        } catch (error) {
            console.error('Submit Error:', error);
            toast.error(error.response?.data?.message || 'Failed to submit purchase orders');
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        setRows([]);
        setRowIdCounter(1);
        onClose();
    };

    // Define DataGrid columns with inline editing
    const columns = [
        {
            field: 'actions',
            type: 'actions',
            headerName: '',
            width: 50,
            getActions: ({ id }) => [
                <GridActionsCellItem
                    icon={<DeleteIcon />}
                    label="Delete"
                    onClick={() => handleDeleteRow(id)}
                    color="error"
                />
            ]
        },
        {
            field: 'event_id',
            headerName: 'Event ID',
            width: 100,
            editable: false
        },
        {
            field: 'event_name',
            headerName: 'Event *',
            width: 300,
            editable: true,
            renderEditCell: (params) => (
                <EventEditCell 
                    {...params} 
                    api={{ searchEvents }} 
                    onRowUpdate={handleRowUpdate}
                />
            )
        },
        {
            field: 'vendor_id',
            headerName: 'Vendor ID',
            width: 100,
            editable: false
        },
        {
            field: 'section',
            headerName: 'Section *',
            width: 120,
            editable: true
        },
        {
            field: 'row',
            headerName: 'Row *',
            width: 100,
            editable: true
        },
        {
            field: 'low_seat',
            headerName: 'Low Seat *',
            width: 100,
            editable: true,
            type: 'number'
        },
        {
            field: 'high_seat',
            headerName: 'High Seat',
            width: 100,
            editable: true,
            type: 'number'
        },
        {
            field: 'quantity',
            headerName: 'Qty *',
            width: 80,
            type: 'number',
            editable: true
        },
        {
            field: 'retail_price',
            headerName: 'Retail Price',
            width: 130,
            type: 'number',
            editable: true,
            valueFormatter: (params) => params ? `$${parseFloat(params).toFixed(2)}` : '$0.00',
            description: 'At least one price (Retail, Wholesale, or Face) must be > 0'
        },
        {
            field: 'wholesale_price',
            headerName: 'Wholesale',
            width: 130,
            type: 'number',
            editable: true,
            valueFormatter: (params) => params ? `$${parseFloat(params).toFixed(2)}` : '$0.00',
            description: 'At least one price (Retail, Wholesale, or Face) must be > 0'
        },
        {
            field: 'face_price',
            headerName: 'Face Price',
            width: 130,
            type: 'number',
            editable: true,
            valueFormatter: (params) => params ? `$${parseFloat(params).toFixed(2)}` : '$0.00',
            description: 'At least one price (Retail, Wholesale, or Face) must be > 0'
        },
        {
            field: 'seating_type',
            headerName: 'Seating Type *',
            width: 150,
            editable: true,
            type: 'singleSelect',
            valueOptions: LOOKUP_OPTIONS.seating_type,
            getOptionValue: (option) => option.id,
            getOptionLabel: (option) => option.label
        },
        {
            field: 'stock_type',
            headerName: 'Stock Type',
            width: 180,
            editable: true,
            type: 'singleSelect',
            valueOptions: LOOKUP_OPTIONS.stock_type,
            getOptionValue: (option) => option.id,
            getOptionLabel: (option) => option.label,
            description: 'Defaults to 1 (Standard) if not provided'
        },
        {
            field: 'ticket_group_type',
            headerName: 'Ticket Type *',
            width: 150,
            editable: true,
            type: 'singleSelect',
            valueOptions: LOOKUP_OPTIONS.ticket_group_type,
            getOptionValue: (option) => option.id,
            getOptionLabel: (option) => option.label
        },
        {
            field: 'exposure_type',
            headerName: 'Exposure *',
            width: 150,
            editable: true,
            type: 'singleSelect',
            valueOptions: LOOKUP_OPTIONS.exposure_type,
            getOptionValue: (option) => option.id,
            getOptionLabel: (option) => option.label
        },
        {
            field: 'split_rule',
            headerName: 'Split Rule',
            width: 120,
            editable: true,
            type: 'singleSelect',
            valueOptions: LOOKUP_OPTIONS.split_rule,
            getOptionValue: (option) => option.id,
            getOptionLabel: (option) => option.label
        },
        {
            field: 'shipping_method',
            headerName: 'Shipping Method *',
            width: 200,
            editable: true,
            type: 'singleSelect',
            valueOptions: LOOKUP_OPTIONS.shipping_method,
            getOptionValue: (option) => option.id,
            getOptionLabel: (option) => option.label,
            description: 'Required at PO level'
        },
        {
            field: 'payment_type',
            headerName: 'Payment Type *',
            width: 150,
            editable: true,
            type: 'singleSelect',
            valueOptions: LOOKUP_OPTIONS.payment_type,
            getOptionValue: (option) => option.id,
            getOptionLabel: (option) => option.label,
            description: 'Required at PO level'
        },
        {
            field: 'shipping_method_special',
            headerName: 'Special Shipping',
            width: 180,
            editable: true,
            type: 'singleSelect',
            valueOptions: LOOKUP_OPTIONS.shipping_method_special,
            getOptionValue: (option) => option.id,
            getOptionLabel: (option) => option.label
        },
        {
            field: 'near_term_display_option',
            headerName: 'Near Term Display',
            width: 180,
            editable: true,
            type: 'singleSelect',
            valueOptions: LOOKUP_OPTIONS.near_term_display_option,
            getOptionValue: (option) => option.id,
            getOptionLabel: (option) => option.label
        },
        {
            field: 'on_hand',
            headerName: 'On Hand',
            width: 100,
            editable: true,
            type: 'boolean',
            description: 'Are tickets physically on hand?'
        },
        {
            field: 'on_hand_date',
            headerName: 'On Hand Date',
            width: 130,
            editable: true,
            type: 'date',
            valueGetter: (params) => params ? new Date(params) : null,
            description: 'Required if tickets are not on hand'
        },
        {
            field: 'ticket_group_cost',
            headerName: 'TG Cost',
            width: 130,
            type: 'number',
            editable: true,
            valueFormatter: (params) => params ? `$${parseFloat(params).toFixed(2)}` : '$0.00'
        },
        {
            field: 'taxed_cost',
            headerName: 'Taxed Cost',
            width: 130,
            type: 'number',
            editable: true,
            valueFormatter: (params) => params ? `$${parseFloat(params).toFixed(2)}` : '$0.00'
        },
        {
            field: 'msrp',
            headerName: 'MSRP',
            width: 130,
            type: 'number',
            editable: true,
            valueFormatter: (params) => params ? `$${parseFloat(params).toFixed(2)}` : '$0.00'
        },
        {
            field: 'external_po_number',
            headerName: 'External PO#',
            width: 150,
            editable: true
        },
        {
            field: 'internal_po_notes',
            headerName: 'Internal PO Notes',
            width: 200,
            editable: true
        },
        {
            field: 'external_po_notes',
            headerName: 'External PO Notes',
            width: 200,
            editable: true
        },
        {
            field: 'internal_tg_notes',
            headerName: 'Internal TG Notes',
            width: 200,
            editable: true
        },
        {
            field: 'external_tg_notes',
            headerName: 'External TG Notes',
            width: 200,
            editable: true
        },
        {
            field: 'pricing_notes',
            headerName: 'Pricing Notes',
            width: 200,
            editable: true
        },
        {
            field: 'broadcast_channel_ids',
            headerName: 'Broadcast Channels',
            width: 180,
            editable: true,
            description: 'Comma-separated broadcast channel IDs',
            renderCell: (params) => (
                <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                    {params.value || '1,2,3,4,6'}
                </Typography>
            )
        }
    ];
    
    const handleEventSelection = (rowIndex, event) => {
        setPendingImport(prev => 
            prev.map((row, idx) => 
                idx === rowIndex 
                    ? { ...row, selectedEvent: event, confirmed: true }
                    : row
            )
        );
    };
    
    const handleConfirmImport = () => {
        const allConfirmed = pendingImport.every(row => row.confirmed && row.selectedEvent);
        if (!allConfirmed) {
            toast.error('Please select an event for all rows before importing');
            return;
        }
        importConfirmedRows(pendingImport);
    };

    return (
        <>
            {/* Event Matching Confirmation Dialog */}
            <Dialog 
                open={matchingDialogOpen} 
                onClose={() => setMatchingDialogOpen(false)} 
                maxWidth="lg" 
                fullWidth
            >
                <DialogTitle>
                    Confirm Event Matches
                    <IconButton
                        onClick={() => setMatchingDialogOpen(false)}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Please confirm the event matches for your CSV import. Select the correct event for each row.
                    </Alert>
                    {pendingImport.map((row, index) => (
                        <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                <strong>Row {index + 1}:</strong> {row.csvRow.event_name} 
                                {row.csvRow.event_date && ` | ${row.csvRow.event_date}`}
                                {row.csvRow.event_venue && ` | ${row.csvRow.event_venue}`}
                            </Typography>
                            
                            <Autocomplete
                                value={row.selectedEvent}
                                onChange={(e, newValue) => handleEventSelection(index, newValue)}
                                onInputChange={async (e, newValue) => {
                                    if (newValue && newValue.length >= 3) {
                                        const results = await searchEvents(newValue);
                                        setPendingImport(prev => prev.map((r, i) => 
                                            i === index ? { ...r, matchedEvents: results } : r
                                        ));
                                    }
                                }}
                                options={row.matchedEvents}
                                getOptionLabel={(option) => option.label}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={row.matchedEvents.length === 0 ? "Search Event Manually" : "Select Event"}
                                        placeholder="Type at least 3 characters..."
                                        size="small"
                                    />
                                )}
                                noOptionsText="Type 3+ characters to search"
                                fullWidth
                            />
                        </Box>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMatchingDialogOpen(false)}>
                        Cancel
                    </Button>
                    <Button 
                        variant="contained" 
                        onClick={handleConfirmImport}
                        disabled={!pendingImport.every(row => row.selectedEvent)}
                    >
                        Import {pendingImport.length} Rows
                    </Button>
                </DialogActions>
            </Dialog>
            
            <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
            <DialogTitle>
                Bulk Purchase Order Entry
                <IconButton
                    onClick={handleClose}
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            
            <DialogContent dividers>
                {(!current_broker || current_broker === 'all') ? (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <AlertTitle>Select a Broker</AlertTitle>
                        Please select a specific broker from the dropdown above to use bulk purchase order entry.
                        This feature is not available when viewing "All Brokers".
                    </Alert>
                ) : (
                    <>
                        <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Button
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={handleDownloadTemplate}
                            >
                                Download Blank Template
                            </Button>
                            
                            <Button
                                variant="outlined"
                                color="secondary"
                                startIcon={<DownloadIcon />}
                                component="a"
                                href="/po_bulk_template_with_examples.csv"
                                download="po_bulk_template_with_examples.csv"
                            >
                                Download Template with Examples
                            </Button>
                            
                            <Button
                                variant="outlined"
                                color="info"
                                component="a"
                                href="/field-guide.html"
                                target="_blank"
                            >
                                View Field Guide
                            </Button>
                            
                            <input
                                accept=".csv"
                                style={{ display: 'none' }}
                                id="csv-upload-input"
                                type="file"
                                onChange={handleCSVUpload}
                            />
                            <label htmlFor="csv-upload-input">
                                <Button
                                    variant="outlined"
                                    component="span"
                                    startIcon={<UploadFileIcon />}
                                >
                                    Import from CSV
                                </Button>
                            </label>
                            
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={handleAddRow}
                            >
                                Add Row
                            </Button>
                        </Box>

                        <Alert severity="info" sx={{ mb: 2 }}>
                            <AlertTitle>Instructions</AlertTitle>
                            <strong>Required Fields (*):</strong> Event, Section, Row, Low Seat, Quantity, Seating Type, Ticket Type, Exposure, Shipping Method, Payment Type, and <strong>at least ONE price</strong> (Retail, Wholesale, or Face) must be &gt; 0.
                            <br />
                            <strong>On Hand Date:</strong> Required if "On Hand" is unchecked (tickets not yet received). Cannot be in the future if "On Hand" is checked.
                            <br />
                            <strong>Parking:</strong> Split Rule automatically sets to "No Splits" when Ticket Type is Parking.
                            <br />
                            <strong>Editing:</strong> Double-click cells to edit. Use dropdown selections for lookup fields. Hover over column headers for field descriptions.
                        </Alert>

                        <Box sx={{ height: 600, width: '100%' }}>
                            <DataGridPro
                                rows={rows}
                                columns={columns}
                                processRowUpdate={handleProcessRowUpdate}
                                slots={{ toolbar: GridToolbar }}
                                slotProps={{
                                    toolbar: {
                                        showQuickFilter: true,
                                    },
                                }}
                                density="compact"
                                editMode="cell"
                                sx={{
                                    '& .MuiDataGrid-cell--editable': {
                                        bgcolor: (theme) => theme.palette.mode === 'dark' ? '#4a4a4a' : '#f5f5f5',
                                    },
                                    '& .MuiDataGrid-row': {
                                        bgcolor: 'white',
                                        '&:hover': {
                                            bgcolor: '#f5f5f5',
                                        }
                                    }
                                }}
                            />
                        </Box>
                    </>
                )}
            </DialogContent>
            
            <DialogActions>
                <Button onClick={handleClose} disabled={submitting}>
                    Cancel
                </Button>
                {current_broker && current_broker !== 'all' && (
                    <Button
                        variant="contained"
                        onClick={validateAndSubmit}
                        disabled={submitting}
                    >
                        {submitting ? <CircularProgress size={24} /> : `Submit ${rows.filter(r => r.event_id || r.section).length} Orders`}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
        
        {/* Submission Results Dialog */}
        <Dialog 
            open={resultsDialogOpen} 
            onClose={() => {
                setResultsDialogOpen(false);
                // If all successful, close main dialog too
                const errorCount = submissionResults.filter(r => r.status === 'error').length;
                if (errorCount === 0) {
                    handleClose();
                }
            }} 
            maxWidth="lg" 
            fullWidth
        >
            <DialogTitle>
                Submission Results
                <IconButton
                    onClick={() => {
                        setResultsDialogOpen(false);
                        const errorCount = submissionResults.filter(r => r.status === 'error').length;
                        if (errorCount === 0) {
                            handleClose();
                        }
                    }}
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            
            <DialogContent dividers>
                <Box sx={{ mb: 2 }}>
                    <Alert 
                        severity={
                            submissionResults.filter(r => r.status === 'error').length === 0 
                                ? 'success' 
                                : submissionResults.filter(r => r.status === 'success').length > 0 
                                    ? 'warning' 
                                    : 'error'
                        }
                    >
                        <AlertTitle>Summary</AlertTitle>
                        <strong>Total:</strong> {submissionResults.length} rows processed
                        <br />
                        <strong>Successful:</strong> {submissionResults.filter(r => r.status === 'success').length} purchase orders created
                        <br />
                        <strong>Failed:</strong> {submissionResults.filter(r => r.status === 'error').length} errors
                    </Alert>
                </Box>
                
                <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
                    {submissionResults.map((result, index) => (
                        <Box
                            key={index}
                            sx={{
                                p: 1.5,
                                mb: 1,
                                border: 1,
                                borderColor: result.status === 'success' ? 'success.main' : 'error.main',
                                borderRadius: 1,
                                backgroundColor: result.status === 'success' ? '#f1f8f4' : '#fef5f5',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2
                            }}
                        >
                            {/* Status Icon */}
                            <Box sx={{ 
                                minWidth: 40, 
                                height: 40, 
                                borderRadius: '50%',
                                backgroundColor: result.status === 'success' ? 'success.main' : 'error.main',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '1.2em',
                                fontWeight: 'bold'
                            }}>
                                {result.status === 'success' ? '✓' : '✗'}
                            </Box>
                            
                            {/* Content */}
                            <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        Row {result.rowNumber}
                                        {result.status === 'success' && (
                                            <Typography component="span" variant="body2" sx={{ ml: 2, color: 'success.dark', fontWeight: 'normal' }}>
                                                PO #{result.poId}
                                            </Typography>
                                        )}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        Qty: {result.quantity}
                                    </Typography>
                                </Box>
                                
                                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                    {result.eventName || 'No Event'} • Sec {result.section}, Row {result.row}
                                </Typography>
                                
                                {result.status === 'error' && (
                                    <Typography variant="body2" color="error.dark" sx={{ mt: 1, fontWeight: 500 }}>
                                        {result.error}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    ))}
                </Box>
            </DialogContent>
            
            <DialogActions>
                <Button 
                    onClick={() => {
                        setResultsDialogOpen(false);
                        const errorCount = submissionResults.filter(r => r.status === 'error').length;
                        if (errorCount === 0) {
                            handleClose();
                        }
                    }}
                    variant="contained"
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
        </>
    );
}

export default POCSVUpload;
